import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useDeferredValue, useState } from "react";
import { GridList, GridListItem } from "react-aria-components";
import { FaCircleCheck, FaXmark } from "react-icons/fa6";
import { css } from "../../../styled-system/css";
import { Flex, HStack, styled, VStack, Wrap } from "../../../styled-system/jsx";
import { flex, grid } from "../../../styled-system/patterns";
import { Button } from "../../components/core/basic/Button";
import { ProductImageURL } from "../../components/custom/category/listings/ProductImage";
import { SearchInput } from "../../components/fields/base/TextField";
import { useCategoriesQuery } from "../../hooks/useCategories";
import { useAppSelector } from "../../redux/hooks";
import getURLPrefix from "../../utils/getUrlPrefix";
import type { Category } from "../../zodios/apiDefinitions/gvads";
import { Modal } from "../../components/core/basic/RACModal";

const URL_PREFIX = getURLPrefix();

interface Props {
  selectedCategories: number[];
  setSelectedCategories: (categories: number[]) => void;

  getIrrelevantCategoriesHiddenText?: (numOfCategories: number) => string;
  sortCategories?: (a: Category, b: Category) => number;
  hideIrrelevantCategories?: (category: Category) => boolean;
  getDescription?: (category: Category) => string;
}

function CategorySelector({
  selectedCategories,
  setSelectedCategories,

  getIrrelevantCategoriesHiddenText = (num) => `${num} categories not shown`,
  sortCategories = (a, b) => a.leaf_category_name.localeCompare(b.leaf_category_name),
  hideIrrelevantCategories = () => false,
  getDescription = () => "",
}: Props) {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const categoryQuery = useCategoriesQuery(marketPlace, channel);

  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);

  const irrelevantCategories = categoryQuery.data?.filter(hideIrrelevantCategories).map(({ id }) => id) ?? [];

  if (categoryQuery.isLoading) {
    return "Loading...";
  }
  if (categoryQuery.isError) {
    return "Error loading categories";
  }
  if (categoryQuery.isSuccess) {
    return (
      <VStack gap="medium" alignItems={"stretch"}>
        <Wrap gap="medium">
          <SearchInput
            placeholder="Search categories..."
            value={query}
            onChange={(value) => {
              setQuery(value);
            }}
            onClear={() => {
              setQuery("");
            }}
            className={css({ flex: "0 1 450px" })}
          />
          <Button variant="link" onPress={() => setSelectedCategories([])} isDisabled={!selectedCategories.length}>
            Clear selection
          </Button>
          {!!selectedCategories.length && (
            <span className={flex({ alignItems: "center" })}>{selectedCategories.length} categories selected</span>
          )}
          {irrelevantCategories.length > 0 && (
            <span className={flex({ alignItems: "center", color: "text.secondary" })}>
              {getIrrelevantCategoriesHiddenText(irrelevantCategories.length)}
            </span>
          )}
        </Wrap>
        {!!selectedCategories.length && (
          <Wrap>
            {categoryQuery.data
              .filter((c) => selectedCategories.includes(c.id))
              .map((category) => (
                <Button
                  variant="link"
                  key={category.id}
                  onPress={() => setSelectedCategories(selectedCategories.filter((c) => c !== category.id))}
                >
                  {category.leaf_category_name}
                  <FaXmark />
                </Button>
              ))}
          </Wrap>
        )}
        <GridList
          aria-label={"Category filter"}
          className={grid({
            minChildWidth: "200px",
            maxHeight: "60vh",
            padding: "2px", // So the outline is visible when keyboard navigationg
            scrollbarColor: "{colors.lightgrey} transparent",
            scrollbarWidth: "thin",
            overflowY: "auto",
          })}
          selectionMode={"multiple"}
          layout={"grid"}
          items={categoryQuery.data
            .filter(
              (category) =>
                category.leaf_category_name.toLowerCase().includes(deferredQuery.toLowerCase()) &&
                !irrelevantCategories.includes(category.id),
            )
            .sort(sortCategories)}
          selectedKeys={selectedCategories}
          onSelectionChange={(selectedKeys) => {
            setSelectedCategories(selectedKeys === "all" ? [] : (Array.from(selectedKeys) as number[]));
          }}
        >
          {(category) => <CategoryItem key={category.id} category={category} description={getDescription(category)} />}
        </GridList>
      </VStack>
    );
  }
}

const CategoryCard = styled(GridListItem, {
  base: {
    all: "unset",
    cursor: "pointer",
    display: "flex",
    position: "relative",
    gap: "small",
    flexDirection: "column",
    justifyContent: "space-between",
    border: "1px solid {colors.lightgrey}",
    borderRadius: "standard",
    padding: "{radii.standard}",
    "&[data-selected]": {
      borderColor: "trellisPalette.3",
    },
    "&[data-focus-visible='true']": {
      outline: "2px solid {colors.trellisPalette.3}",
    },
  },
});

function CategoryItem({ category, description }: { category: Category; description: string }) {
  return (
    <CategoryCard id={category.id} textValue={category.leaf_category_name}>
      {(item) => (
        <>
          <Flex gap="medium">
            <ProductImageURL url={`${URL_PREFIX}/api/productimage/?category=${category.id}`} width={50} height={50} />
            <span className={css({ fontSize: "16px", fontWeight: "500" })}>{category.leaf_category_name}</span>
          </Flex>
          <span>{description}</span>
          {item.isSelected && (
            <FaCircleCheck
              className={css({
                position: "absolute",
                right: "small",
                bottom: "small",
                color: "trellisPalette.3",
                fontSize: "xlarge",
              })}
            />
          )}
        </>
      )}
    </CategoryCard>
  );
}

export const CategorySelectorModal = NiceModal.create(
  ({
    initialSelectedCategories,
    onConfirm,
    ...rest
  }: {
    initialSelectedCategories: number[];
    onConfirm: (selectedCategories: number[]) => void;
  } & Omit<Props, "selectedCategories" | "setSelectedCategories">) => {
    const modal = useModal();
    const [selectedCategories, setSelectedCategories] = useState(initialSelectedCategories);

    return (
      <Modal
        className={css({
          minWidth: "80vw",
          maxWidth: "unset",
          maxHeight: "unset",
          padding: "large",
        })}
        isDismissable
        isOpen={modal.visible}
        title={"Choose Categories"}
        onOpenChange={(isOpen) => !isOpen && modal.remove()}
      >
        <CategorySelector
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          {...rest}
        />

        <HStack gap="medium" justifyContent={"space-around"}>
          <Button variant="secondary" onPress={() => modal.remove()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onPress={() => {
              onConfirm(selectedCategories);
              modal.remove();
            }}
          >
            Apply
          </Button>
        </HStack>
      </Modal>
    );
  },
);
